import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { GenomeStats } from '../lib/stats';
import { CHART_COLORS, type Theme } from '../lib/theme';
import { Explain } from './Explain';

interface Props {
  stats: GenomeStats;
  theme: Theme;
}

export function Analysis({ stats, theme }: Props) {
  const c = CHART_COLORS[theme];
  const tooltipStyle = {
    background: c.tooltipBg,
    border: `1px solid ${c.tooltipBorder}`,
    borderRadius: 10,
    color: c.tooltipText,
  };

  const zygosity = [
    { label: 'Homozygous', count: stats.homozygous },
    { label: 'Heterozygous', count: stats.heterozygous },
    { label: 'No-call', count: stats.noCalls },
  ];

  return (
    <>
      <div className="two-col">
        <div className="card">
          <h2>Genotype distribution<Explain id="genotypeDist" /></h2>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={stats.genotypeCounts} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: c.axis, fontSize: 11 }}
                  axisLine={{ stroke: c.grid }} tickLine={false} interval={0} />
                <YAxis tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false}
                  width={48}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                <Tooltip
                  cursor={{ fill: c.cursor }}
                  contentStyle={tooltipStyle}
                  formatter={(v: number) => [v.toLocaleString(), 'variants']}
                />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {stats.genotypeCounts.map((_, i) => (
                    <Cell key={i} fill={c.palette[i % c.palette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h2>Zygosity<Explain id="zygosity" /></h2>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={zygosity} layout="vertical" margin={{ top: 8, right: 12, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} horizontal={false} />
                <XAxis type="number" tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                <YAxis type="category" dataKey="label" tick={{ fill: c.axis, fontSize: 11 }}
                  axisLine={false} tickLine={false} width={96} />
                <Tooltip
                  cursor={{ fill: c.cursor }}
                  contentStyle={tooltipStyle}
                  formatter={(v: number) => [v.toLocaleString(), 'variants']}
                />
                <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                  {zygosity.map((_, i) => (
                    <Cell key={i} fill={c.palette[i % c.palette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Quality metrics<Explain id="quality" /></h2>
        <div className="stat-grid">
          <div className="stat">
            <div className="label">Transitions</div>
            <div className="value">{stats.transitions.toLocaleString()}</div>
          </div>
          <div className="stat">
            <div className="label">Transversions</div>
            <div className="value">{stats.transversions.toLocaleString()}</div>
          </div>
          <div className="stat">
            <div className="label">Ti/Tv</div>
            <div className="value accent">{stats.tiTv === null ? '—' : stats.tiTv.toFixed(3)}</div>
            <div className="sub">~2.0–2.1 genome-wide is typical</div>
          </div>
          <div className="stat">
            <div className="label">Called sites</div>
            <div className="value">{stats.called.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className="note" style={{ marginTop: 18 }}>
        <strong>Not a medical tool.</strong> This tab reports structural and
        quality metrics about your data only. The Conditions tab reports
        published research associations — statistical findings about groups, not
        conclusions about you. Neither is medical advice; for clinical questions,
        talk to a qualified professional.
      </div>
    </>
  );
}
